/**
 * Korean particle selection.
 *
 * A pure orthographic utility with no domain knowledge in it, kept here because
 * two layers write Korean and both need the same answer: the server's mock
 * agents, whose summaries and memory lines are Korean prose with province and
 * good names interpolated into them, and the browser, whose entire UI is Korean.
 * Duplicating it would let the two drift on the same word.
 *
 * It does not make the core speak Korean. Event headlines and refusal reasons
 * stay English there, because those strings are also server logs and agent
 * context.
 */

export type ParticlePair = "은는" | "이가" | "을를" | "와과" | "로으로";

const FORMS: Record<ParticlePair, [noFinal: string, hasFinal: string, both: string]> = {
  은는: ["는", "은", "은(는)"],
  이가: ["가", "이", "이(가)"],
  을를: ["를", "을", "을(를)"],
  와과: ["와", "과", "와(과)"],
  // ㄹ takes 로, not 으로 — the one pair where the rule is not a plain
  // has-batchim test. 간트록**으로**, but 서울**로**.
  로으로: ["로", "으로", "(으)로"],
};

const HANGUL_BASE = 0xac00;
const HANGUL_COUNT = 11172;
/** Jongseong index of ㄹ within a composed Hangul syllable. */
const JONGSEONG_RIEUL = 8;

/**
 * Appends the right half of a particle pair, chosen from the word in front.
 *
 * A composed Hangul syllable carries a final consonant when
 * `(code - 0xAC00) % 28` is non-zero, and that single test decides 은/는, 이/가,
 * 을/를 and 와/과. A hard-coded particle is wrong for about half the vocabulary
 * here — 무기**가** but 자원**이**, 위**와** but 한**과** — and the state names
 * split both ways, so every interpolated particle goes through this.
 *
 * A word that does not end in a composed Hangul syllable — a Latin name, a
 * number, a currency string — has no defensible answer, so the ambiguous
 * both-forms spelling is kept rather than guessed at.
 */
export function withParticle(word: string, pair: ParticlePair): string {
  const [noFinal, hasFinal, both] = FORMS[pair];
  const index = word.charCodeAt(word.length - 1) - HANGUL_BASE;
  if (!Number.isFinite(index) || index < 0 || index >= HANGUL_COUNT) return `${word}${both}`;

  const final = index % 28;
  if (pair === "로으로") {
    return `${word}${final === 0 || final === JONGSEONG_RIEUL ? noFinal : hasFinal}`;
  }
  return `${word}${final === 0 ? noFinal : hasFinal}`;
}
